import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { isEmailLikeRecipient } from '@/lib/vault-invitations';
import { useDebouncedValue } from './useDebouncedValue';

/**
 * Debounced platform user-directory search (GET /users/search) for
 * username autocomplete. Disabled until the query is at least 2
 * characters, and for email-like input (an "@" after the first
 * character means the user is typing an address, not a handle — a
 * leading "@" is the handle sigil and is stripped).
 *
 * `isDebouncing` is true while the typed query is newer than the one
 * the request layer sees — consumers show "Searching…" for it so the
 * empty state can't flash before a request has even fired.
 */
export function useUserSearch(rawQuery: string) {
  const query = rawQuery.trim().replace(/^@/, '');
  const debounced = useDebouncedValue(query, 250);
  const searchable = query.length >= 2 && !isEmailLikeRecipient(rawQuery);

  const result = useQuery({
    queryKey: queryKeys.users.search(debounced),
    queryFn: () => api.searchUsers(debounced),
    select: (data) => data.users,
    enabled: debounced.length >= 2 && !debounced.includes('@'),
    staleTime: 30_000,
  });

  return { ...result, isDebouncing: searchable && query !== debounced };
}
